
/**
 * Gets the GMT offset for a timezone at the current date
 * @param {string} timezone - IANA timezone string (e.g., 'America/New_York')
 * @returns {string} - Formatted GMT offset (e.g., 'GMT+2', 'GMT-4')
 */
const getGmtOffset = (timezone) => {
  try {
    // This method properly accounts for Daylight Saving Time by using the Intl API
    // It will always return the current offset at the time the function is called
    
    // Create a date object to represent right now
    const date = new Date();
    
    // Format the date with the specific timezone to extract the GMT offset
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
    
    // Extract the GMT offset part from the formatted date
    const formattedDate = formatter.format(date);
    const gmtMatch = formattedDate.match(/GMT[+-]\d+(?::?\d+)?/);
    
    if (gmtMatch) {
      // Handle formatting for consistent display
      const gmtPart = gmtMatch[0];
      // If offset has minutes like GMT+5:30, keep as is
      if (gmtPart.includes(':')) {
        return gmtPart;
      }
      // Otherwise, display just hours like GMT+2
      return gmtPart.replace(/:00/, '');
    }
    
    // Fallback method if the Intl formatter approach doesn't extract the offset
    // This determines the offset by comparing the local time to the time in the target timezone
    const targetTime = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const utcTime = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    
    // Calculate the difference in hours
    const offsetMinutes = (targetTime - utcTime) / (1000 * 60);
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetRemainingMinutes = Math.abs(offsetMinutes) % 60;
    
    // Format the output
    const sign = offsetMinutes >= 0 ? '+' : '-';
    
    // Include minutes only if they're not zero
    if (offsetRemainingMinutes === 0) {
      return `GMT${sign}${offsetHours}`;
    } else {
      return `GMT${sign}${offsetHours}:${offsetRemainingMinutes.toString().padStart(2, '0')}`;
    }
  } catch (error) {
    console.error('Error calculating timezone offset for', timezone, error);
    // Fallback if timezone is not recognized
    return '';
  }
};

/**
 * Create a city timezone option with GMT offset in the label
 * @param {string} city - City name
 * @param {string} country - Country name
 * @param {string} timezone - IANA timezone
 * @param {string} timezoneName - Human readable timezone name (e.g., Pacific Time)
 * @returns {Object} - City timezone option object
 */
const createCityTimezoneOption = (city, country, timezone, timezoneName) => {
  const gmtOffset = getGmtOffset(timezone);
  const gmtInfo = gmtOffset ? ` (${gmtOffset})` : '';
  const label = `${city}, ${country} (${timezoneName}${gmtInfo})`;
  
  return {
    city,
    country,
    timezone,
    label
  };
};

const cityTimezoneOptions = [
  // North America
  createCityTimezoneOption('San Francisco', 'USA', 'America/Los_Angeles', 'Pacific Time'),
  createCityTimezoneOption('Redwood City', 'USA', 'America/Los_Angeles', 'Pacific Time'),
  createCityTimezoneOption('Marina del Rey', 'USA', 'America/Los_Angeles', 'Pacific Time'),
  createCityTimezoneOption('Denver', 'USA', 'America/Denver', 'Mountain Time'),
  createCityTimezoneOption('Austin', 'USA', 'America/Chicago', 'Central Time'),
  createCityTimezoneOption('Chicago', 'USA', 'America/Chicago', 'Central Time'),
  createCityTimezoneOption('Charlotte', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('Raleigh', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('Boston', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('New York', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('Washington, D.C.', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('Miami', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('Atlanta', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('Manchester', 'USA', 'America/New_York', 'Eastern Time'),
  createCityTimezoneOption('Toronto', 'Canada', 'America/Toronto', 'Eastern Time'),
  createCityTimezoneOption('Montreal', 'Canada', 'America/Toronto', 'Eastern Time'),
  createCityTimezoneOption('Mexico City', 'Mexico', 'America/Mexico_City', 'Central Time'),
  createCityTimezoneOption('São Paulo', 'Brazil', 'America/Sao_Paulo', 'Brasilia Time'),

  // Europe
  createCityTimezoneOption('London', 'UK', 'Europe/London', 'GMT'),
  createCityTimezoneOption('Reading', 'UK', 'Europe/London', 'GMT'),
  createCityTimezoneOption('Cardiff', 'UK', 'Europe/London', 'GMT'),
  createCityTimezoneOption('Dublin', 'Ireland', 'Europe/Dublin', 'GMT'),
  createCityTimezoneOption('Paris', 'France', 'Europe/Paris', 'CET'),
  createCityTimezoneOption('Berlin', 'Germany', 'Europe/Berlin', 'CET'),
  createCityTimezoneOption('Amsterdam', 'Netherlands', 'Europe/Amsterdam', 'CET'),
  createCityTimezoneOption('Vienna', 'Austria', 'Europe/Vienna', 'CET'),
  createCityTimezoneOption('Oslo', 'Norway', 'Europe/Oslo', 'CET'),
  createCityTimezoneOption('Stockholm', 'Sweden', 'Europe/Stockholm', 'CET'),
  createCityTimezoneOption('Gothenburg', 'Sweden', 'Europe/Stockholm', 'CET'),
  createCityTimezoneOption('Helsinki', 'Finland', 'Europe/Helsinki', 'EET'),
  createCityTimezoneOption('Tampere', 'Finland', 'Europe/Helsinki', 'EET'),
  createCityTimezoneOption('Budapest', 'Hungary', 'Europe/Budapest', 'CET'),

  // Africa
  createCityTimezoneOption('Johannesburg', 'South Africa', 'Africa/Johannesburg', 'SAST'),
  createCityTimezoneOption('Cape Town', 'South Africa', 'Africa/Johannesburg', 'SAST'),
  createCityTimezoneOption('Accra', 'Ghana', 'Africa/Accra', 'GMT'),

  // Asia
  createCityTimezoneOption('Dubai', 'UAE', 'Asia/Dubai', 'Gulf Standard Time'),
  createCityTimezoneOption('Abu Dhabi', 'UAE', 'Asia/Dubai', 'Gulf Standard Time'),
  createCityTimezoneOption('New Delhi', 'India', 'Asia/Kolkata', 'IST'),
  createCityTimezoneOption('Mumbai', 'India', 'Asia/Kolkata', 'IST'),
  createCityTimezoneOption('Haifa', 'Israel', 'Asia/Jerusalem', 'Israel Time'),
  createCityTimezoneOption('Shanghai', 'China', 'Asia/Shanghai', 'CST'),
  createCityTimezoneOption('Hong Kong', 'Hong Kong', 'Asia/Hong_Kong', 'HKT'),
  createCityTimezoneOption('Tokyo', 'Japan', 'Asia/Tokyo', 'JST'),
  createCityTimezoneOption('Kuala Lumpur', 'Malaysia', 'Asia/Kuala_Lumpur', 'MYT'),
  createCityTimezoneOption('Singapore', 'Singapore', 'Asia/Singapore', 'SGT'),

  // Oceania
  createCityTimezoneOption('Sydney', 'Australia', 'Australia/Sydney', 'AET'),
  createCityTimezoneOption('Melbourne', 'Australia', 'Australia/Melbourne', 'AET'),
  createCityTimezoneOption('Auckland', 'New Zealand', 'Pacific/Auckland', 'NZST')
];

// Make the options searchable by timezone and GMT offset
cityTimezoneOptions.forEach(option => {
  // Add searchable properties that won't be displayed but can be used for filtering
  option.searchTerms = [
    option.city.toLowerCase(),
    option.country.toLowerCase(),
    option.timezone.toLowerCase(),
    option.label.toLowerCase(),
    getGmtOffset(option.timezone).toLowerCase()
  ].join(' ');
});

export { cityTimezoneOptions, getGmtOffset };
export default cityTimezoneOptions;
